export type CurrencyCode = 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD';

export const DEFAULT_CURRENCY: CurrencyCode = 'USD';

const CURRENCY_CODES = new Set<string>(['USD', 'EUR', 'GBP', 'CAD', 'AUD']);

export interface Money {
  amount: number;
  currency: CurrencyCode;
}

export function zero(currency: CurrencyCode): Money {
  return { amount: 0, currency };
}

export function add(left: Money, right: Money): Money {
  assertSameCurrency(left, right);
  return { amount: left.amount + right.amount, currency: left.currency };
}

export function sub(left: Money, right: Money): Money {
  assertSameCurrency(left, right);
  return { amount: left.amount - right.amount, currency: left.currency };
}

export function mul(money: Money, factor: number): Money {
  return { amount: money.amount * factor, currency: money.currency };
}

export function compare(left: Money, right: Money): number {
  assertSameCurrency(left, right);
  if (left.amount === right.amount) return 0;
  return left.amount > right.amount ? 1 : -1;
}

export function format(money: Money, locale: string): string {
  return formatCurrencyAmount(money.amount, money.currency, locale);
}

/** Formats a numeric amount as currency; invalid codes fall back to USD. */
export function formatCurrencyAmount(amount: number, currency: string, locale: string): string {
  const code = resolveCurrencyCode(currency);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: DEFAULT_CURRENCY,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }
}

/**
 * Resolves currency from preview context (`quote.currency` or top-level `currency`).
 * When the record has none, uses optional `fallbackCurrency` (org viewer locale), then USD.
 */
export function resolveDocumentCurrency(
  context: Record<string, unknown> | null | undefined,
  fallbackCurrency?: string | null
): CurrencyCode {
  if (!context) {
    return resolveFallbackCurrency(fallbackCurrency);
  }

  const quote = context.quote;
  if (typeof quote === 'object' && quote !== null && !Array.isArray(quote)) {
    const fromQuote = (quote as Record<string, unknown>).currency;
    if (typeof fromQuote === 'string' && fromQuote.trim()) {
      return resolveCurrencyCode(fromQuote);
    }
  }

  if (typeof context.currency === 'string' && context.currency.trim()) {
    return resolveCurrencyCode(context.currency);
  }

  return resolveFallbackCurrency(fallbackCurrency);
}

/**
 * Narrows an untrusted currency string to the closed `CurrencyCode` union.
 * Empty / unknown codes → `DEFAULT_CURRENCY` (keeps Money arithmetic type-safe).
 */
export function resolveCurrencyCode(currency: string | null | undefined): CurrencyCode {
  const trimmed = currency?.trim();
  if (!trimmed) return DEFAULT_CURRENCY;
  const upper = trimmed.toUpperCase();
  if (CURRENCY_CODES.has(upper)) {
    return upper as CurrencyCode;
  }
  return DEFAULT_CURRENCY;
}

function resolveFallbackCurrency(fallbackCurrency: string | null | undefined): CurrencyCode {
  if (typeof fallbackCurrency === 'string' && fallbackCurrency.trim()) {
    return resolveCurrencyCode(fallbackCurrency);
  }
  return DEFAULT_CURRENCY;
}

function assertSameCurrency(left: Money, right: Money): void {
  if (left.currency !== right.currency) {
    throw new Error(`Currency mismatch: ${left.currency} vs ${right.currency}`);
  }
}
